import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del } from '../../api/base';
import { ProjectType, TreeItemType } from '../../types/types';

interface ProjectState {
  project: ProjectType;
  currentProject: ProjectType;
  currentProjectTreeView: TreeItemType;
  projects: ProjectType[];
  loading: boolean;
  error: any;
}

export const initialState: ProjectState = {
  project: {
    id: null,
    name: '',
    company: null,
    created_by: null,
    customer: '',
    business_unit: '',
    facility: '',
    description: '',
  },
  currentProject: {
    id: null,
    name: '',
    company: null,
    created_by: null,
    customer: '',
    business_unit: '',
    facility: '',
    description: '',
  },
  currentProjectTreeView: {
    id: null,
    name: '',
    type: 'project',
    children: [],
  },
  projects: [],
  loading: false,
  error: null,
};

export const getAllProjects = createAsyncThunk('projects/getAllProjects', async (_, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    const response = await get('projects/', {
      headers: headers,
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const getProject = createAsyncThunk('projects/getProject', async (id: number, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    const response = await get(`projects/${id}/`, {
      headers: headers,
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const getProjectTreeView = createAsyncThunk(
  'projects/getProjectTreeView',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`project/${id}/tree/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (project: ProjectType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post('projects/', project, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async (project: ProjectType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await put(`projects/${project.id}/`, project, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await del(`projects/${id}/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const cloneProject = createAsyncThunk('projects/cloneProject', async (id: number, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    const response = await post(`projects/${id}/clone/`, {}, {
      headers: headers,
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data);
  }
});

export const setCurrentProject = (project: ProjectType) => {
  return {
    type: 'projects/setCurrentProject',
    payload: project,
  };
};

export const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllProjects.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllProjects.fulfilled, (state, action) => {
      state.projects = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getAllProjects.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(createProject.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createProject.fulfilled, (state, action) => {
      state.projects.push(action.payload);
      state.loading = false;
      state.error = null;
    });
    builder.addCase(createProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(updateProject.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateProject.fulfilled, (state, action) => {
      const index = state.projects.findIndex((project) => project.id === action.payload.id);
      state.projects[index] = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(updateProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(cloneProject.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(cloneProject.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(cloneProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(deleteProject.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteProject.fulfilled, (state, action) => {
      state.projects = state.projects.filter((project) => project.id !== action.payload.id);
      state.loading = false;
      state.error = null;
    });
    builder.addCase(deleteProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getProject.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProject.fulfilled, (state, action) => {
      state.currentProject = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getProjectTreeView.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProjectTreeView.fulfilled, (state, action) => {
      state.currentProjectTreeView = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getProjectTreeView.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export default projectSlice.reducer;
